// Cross-venue divergence badge — a small violet pill shown on a trader's
// order/position when Polymarket's mid has pulled >= 2¢ from Kalshi (and the
// poly book is fresh enough to trust). Self-gating: renders nothing unless the
// gap is active, so every call site is a one-liner. The persistent counterpart
// to the one-shot .animate-divergence-flash card cue. Two shapes, one rule and
// one tooltip: the labelled pill for cards, and a compact dot (`variant="dot"`)
// for table rows too short to seat the pill.

import type { DivergenceInfo } from '@/lib/divergence/polyKalshiDivergence';
import {
  fmtDivergenceBadge,
  divergenceTooltip,
  isDivergenceActive,
} from '@/lib/divergence/polyKalshiDivergence';

export function DivergenceBadge({
  info,
  className = '',
  variant = 'pill',
}: {
  info: DivergenceInfo | null | undefined;
  className?: string;
  /** `'dot'` is the compact, label-free marker for dense table rows. The pill
   *  is taller than a dashboard row's price cell, so on the Value Dashboard it
   *  can only clear the price pills by riding up into the row above. The dot
   *  fits inside the row instead, and carries the SAME `divergenceTooltip`
   *  copy — the signed magnitude moves from the label into the tooltip, which
   *  already stated it. Cards keep the labelled pill (`'pill'`, the default). */
  variant?: 'pill' | 'dot';
}) {
  if (!isDivergenceActive(info)) return null;
  if (variant === 'dot') {
    return (
      <span
        title={divergenceTooltip(info)}
        className={`block h-2 w-2 rounded-full bg-violet-400 light:bg-violet-600 ${className}`}
      />
    );
  }
  return (
    <span
      title={divergenceTooltip(info)}
      className={`inline-flex shrink-0 items-center gap-1 rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-violet-300 light:bg-violet-500/20 light:text-violet-700 ${className}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-violet-400 light:bg-violet-600" />
      {fmtDivergenceBadge(info)}
    </span>
  );
}
